import React, { useState, useEffect } from 'react'
import './App.css'
import { Redirect, useHistory } from 'react-router-dom'
import Axios from 'axios'
import { Bar, Line, Doughnut } from 'react-chartjs-2'
import { API_URL } from './config'
import { IoIosArrowBack, IoIosArrowRoundUp, IoIosArrowRoundDown } from 'react-icons/io'
import { BiMenu } from 'react-icons/bi'
import SideNav from './layouts/SideNav'
import Header from './layouts/Header'

/* ---------- design tokens ---------- */
const C = {
  gen: '#e59a1a',
  vip: '#2f6fed',
  vvip: '#8b3fd9',
  up: '#12a06b',
  down: '#e5484d',
  ink: '#14213d',
  muted: '#6b7590',
  line: '#e6e9f2',
  track: '#eef1f8',
}

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

.an { font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif; color: ${C.ink}; padding: 8px 4px 48px; margin-top:50px }
.an *, .an *::before, .an *::after { box-sizing: border-box; }
.an h1, .an h2, .an h3, .an p { margin: 0; }

.an-back { display: inline-flex; align-items: center; gap: 4px; background: none; border: 0; padding: 4px 8px 4px 0; color: ${C.muted}; font: 600 14px/1 'Manrope', sans-serif; cursor: pointer; }
.an-back:hover { color: ${C.ink}; }
.an-back:focus-visible, .an-tab:focus-visible { outline: 2px solid ${C.vip}; outline-offset: 2px; border-radius: 6px; }

.an-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 12px; margin: 14px 0 24px; }
.an-title { font-size: 28px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; text-align:left !important }
.an-meta { margin-top: 6px; color: ${C.muted}; font-size: 14px; font-weight: 500; }
.an-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; border-radius: 999px; background: #fff; border: 1px solid ${C.line}; font-size: 13px; font-weight: 700; }
.an-dot { width: 8px; height: 8px; border-radius: 50%; }

.an-grid { display: grid; gap: 16px; }
.an-kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 16px; }
.an-two { grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 16px; }
.an-three { grid-template-columns: 1.4fr 1fr; margin-bottom: 16px; }

.an-card { background: #fff; border: 1px solid ${C.line}; border-radius: 14px; padding: 20px; min-width: 0; }
.an-card-h { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 16px; }
.an-card-t { font-size: 15px; font-weight: 700; }
.an-card-s { font-size: 12px; color: ${C.muted}; font-weight: 500; }

.an-kpi-l { font-size: 13px; color: ${C.muted}; font-weight: 600; }
.an-kpi-v { margin-top: 10px; font-size: 30px; font-weight: 800; letter-spacing: -0.02em; line-height: 1; font-variant-numeric: tabular-nums; }
.an-kpi-f { margin-top: 12px; display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: ${C.muted}; font-weight: 600; }
.an-delta { display: inline-flex; align-items: center; font-weight: 700; }

.an-chart { position: relative; height: 260px; }
.an-chart.tall { height: 300px; }
.an-donut { position: relative; height: 240px; }
.an-donut-c { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
.an-donut-c b { font-size: 20px; font-weight: 800; font-variant-numeric: tabular-nums; }
.an-donut-c span { font-size: 12px; color: ${C.muted}; font-weight: 600; }

.an-legend { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 16px; }
.an-legend div { padding-top: 10px; border-top: 3px solid; }
.an-legend small { display: block; font-size: 12px; color: ${C.muted}; font-weight: 600; }
.an-legend b { font-size: 14px; font-weight: 800; font-variant-numeric: tabular-nums; }

.an-tier { padding: 14px 0; border-top: 1px solid ${C.line}; }
.an-tier:first-of-type { border-top: 0; padding-top: 0; }
.an-tier-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 4px; }
.an-tier-n { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; }
.an-tier-p { font-size: 12px; font-weight: 700; padding: 3px 9px; border-radius: 999px; }
.an-tier-sub { display: flex; justify-content: space-between; gap: 8px; font-size: 12.5px; color: ${C.muted}; font-weight: 500; margin-bottom: 8px; font-variant-numeric: tabular-nums; }
.an-track { height: 8px; border-radius: 999px; background: ${C.track}; overflow: hidden; }
.an-fill { height: 100%; border-radius: 999px; transition: width .5s ease; }

.an-movement { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
.an-movement th { text-align: right; font-size: 12px; color: ${C.muted}; font-weight: 600; padding: 0 0 10px; }
.an-movement th:first-child, .an-movement td:first-child { text-align: left; }
.an-movement td { text-align: right; padding: 12px 0; border-top: 1px solid ${C.line}; font-size: 14px; font-weight: 700; }
.an-movement td:first-child { color: ${C.muted}; font-weight: 600; font-size: 13px; }

.an-summary { margin-top: 16px; padding: 14px 16px; border-radius: 10px; display: flex; align-items: center; gap: 10px; font-size: 13.5px; font-weight: 600; }
.an-summary b { font-size: 20px; font-weight: 800; margin-left: auto; }

.an-toggle { display: flex; background: ${C.track}; border-radius: 8px; padding: 3px; }
.an-tab { border: 0; background: transparent; padding: 5px 12px; font: 700 12px/1 'Manrope', sans-serif; color: ${C.muted}; border-radius: 6px; cursor: pointer; }
.an-tab.on { background: #fff; color: ${C.ink}; box-shadow: 0 1px 2px rgba(20,33,61,.12); }

@media (max-width: 1100px) {
  .an-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .an-three { grid-template-columns: 1fr; }
}
@media (max-width: 720px) {
  .an-two { grid-template-columns: 1fr; }
  .an-title { font-size: 23px; }
  .an-kpi-v { font-size: 26px; }
  .an-card { padding: 16px; }
}
@media (max-width: 460px) {
  .an-kpis { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) { .an-fill { transition: none; } }
`

/* ---------- helpers ---------- */
const num = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const money = (amount, symbol = 'R') => {
  const parts = num(amount).toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${symbol}${parts.join('.')}`
}

const compact = (v) => {
  const n = num(v)
  if (n >= 1e6) return `R${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `R${(n / 1e3).toFixed(1)}k`
  return `R${n.toFixed(0)}`
}

const standing = (p) => {
  if (p < 25) return { label: 'Poor', color: C.down, bg: 'rgba(229,72,77,.1)' }
  if (p < 50) return { label: 'Average', color: '#c77d0a', bg: 'rgba(229,154,26,.14)' }
  if (p < 75) return { label: 'Good', color: '#2e8b4a', bg: 'rgba(46,139,74,.12)' }
  return { label: 'Excellent', color: C.up, bg: 'rgba(18,160,107,.12)' }
}

const change = (today, yesterday) => {
  if (yesterday <= 0) return today > 0 ? { pct: null, dir: 'up' } : { pct: 0, dir: 'flat' }
  const pct = ((today - yesterday) / yesterday) * 100
  return { pct, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' }
}

const Delta = ({ d }) => {
  if (d.dir === 'flat')
    return (
      <span className="an-delta" style={{ color: C.muted }}>
        No change
      </span>
    )
  const up = d.dir === 'up'
  return (
    <span className="an-delta" style={{ color: up ? C.up : C.down }}>
      {up ? <IoIosArrowRoundUp size={20} /> : <IoIosArrowRoundDown size={20} />}
      {d.pct === null ? 'New' : `${Math.abs(d.pct).toFixed(1)}%`}
    </span>
  )
}

/* ---------- shared chart options (chart.js v2 syntax) ---------- */
const font = { fontFamily: "'Manrope', sans-serif", fontColor: C.muted, fontSize: 12 }
const tip = {
  backgroundColor: C.ink,
  titleFontFamily: "'Manrope', sans-serif",
  bodyFontFamily: "'Manrope', sans-serif",
  cornerRadius: 8,
  xPadding: 12,
  yPadding: 10,
  displayColors: true,
}
const gridY = { color: C.line, drawBorder: false, zeroLineColor: C.line }
const gridX = { display: false, drawBorder: false }

const Analytics = (props) => {
  const history = useHistory()
  const st = (props.location && props.location.state) || {}

  const [gen, setGen] = useState({ revenue: 0, sold: 0, total: 0, pct: '0%', perc: 0 })
  const [vip, setVip] = useState({ revenue: 0, sold: 0, total: 0, pct: '0%', perc: 0 })
  const [vvip, setVvip] = useState({ revenue: 0, sold: 0, total: 0, pct: '0%', perc: 0 })

  const [totalSales, setTotalSales] = useState(0)
  const [totalSold, setTotalSold] = useState(0)
  const [ticketsAvailable, setTicketsAvailable] = useState(0)
  const [perc, setPerc] = useState(0)
  const [percentage, setPercentage] = useState('0%')

  const [fin, setFin] = useState({
    todayincome: 0,
    yesterdayincome: 0,
    overallincome: 0,
    todaysales: 0,
    yesterdaysales: 0,
    overallsales: 0,
    overallindicators: 0,
    todayPerformance: 0,
    yesterdayPerformance: 0,
  })

  const [metric, setMetric] = useState('revenue')
  const [isMob, setIsMob] = useState(window.innerWidth <= 768)
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 768)

  useEffect(() => {
    const updateIsMobile = () => {
      const mobile = window.innerWidth <= 768
      setIsMob(mobile)
      setCollapsed((prev) => (mobile ? true : prev))
    }

    const businessname = localStorage.getItem('businessname')
    const businessid = localStorage.getItem('businessid')
    const eventname = st.eventname
    const eventid = st.eventid
    document.title = 'Analytics - ' + eventname + ' - ' + businessname

    const base = `${API_URL}/apps/kscanner/webservices.php`
    const get = (query) =>
      Axios.get(`${base}?query=${query}&accountid=${businessid}&eventid=${eventid}`, {
        withCredentials: true,
      })

    get('GETSALESDATAGENERAL')
      .then(({ data }) =>
        setGen({
          revenue: data.general,
          sold: data.salesg,
          total: data.generalTickets,
          pct: data.getPercentage || '0%',
          perc: num(data.getPerc),
        }),
      )
      .catch((e) => console.log(e))

    get('GETSALESDATAVIP')
      .then(({ data }) =>
        setVip({
          revenue: data.vip,
          sold: data.salesv,
          total: data.viptickets,
          pct: data.getPercentage || '0%',
          perc: num(data.getPerc),
        }),
      )
      .catch((e) => console.log(e))

    get('GETSALESDATAVVIP')
      .then(({ data }) =>
        setVvip({
          revenue: data.vvip,
          sold: data.salesvv,
          total: data.vviptickets,
          pct: data.getPercentage || '0%',
          perc: num(data.getPerc),
        }),
      )
      .catch((e) => console.log(e))

    get('GETTOTALSALES')
      .then(({ data }) => setTotalSales(data.sales))
      .catch((e) => console.log(e))

    get('GETTOTALSOLD')
      .then(({ data }) => {
        setTotalSold(data.sold)
        setPerc(num(data.perc))
        setTicketsAvailable(data.ticketsAvailable)
        setPercentage(data.percentage || '0%')
      })
      .catch((e) => console.log(e))

    Axios.get(`${base}?query=FINANCIALS&eventid=${eventid}&accountid=${businessid}`, {
      withCredentials: true,
    })
      .then(({ data }) =>
        setFin({
          todayincome: data.todayincome,
          yesterdayincome: data.yesterdayincome,
          overallincome: data.overallincome,
          todaysales: data.todaysales,
          yesterdaysales: data.yesterdaysales,
          overallsales: data.overallsales,
          overallindicators: data.overallindicators,
          todayPerformance: data.todayPerformance,
          yesterdayPerformance: data.yesterdayPerformance,
        }),
      )
      .catch((e) => console.log(e))

    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
    // eslint-disable-next-line
  }, [])

  const handleToggle = () => setCollapsed((prev) => !prev)

  // Match these to your SideNav CSS widths
  const SIDEBAR_WIDTH = 240
  const SIDEBAR_COLLAPSED_WIDTH = 72
  const sidebarOffset = isMob ? 0 : collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

  /* ---------- derived numbers ---------- */
  const tiers = [
    { key: 'gen', name: 'General', color: C.gen, ...gen },
    { key: 'vip', name: 'VIP', color: C.vip, ...vip },
    { key: 'vvip', name: 'VVIP', color: C.vvip, ...vvip },
  ].map((t) => ({
    ...t,
    revenue: num(t.revenue),
    sold: num(t.sold),
    total: num(t.total),
    remaining: Math.max(num(t.total) - num(t.sold), 0),
    rate: num(String(t.pct).replace('%', '')),
  }))

  const revenueTotal = num(totalSales)
  const soldTotal = num(totalSold)
  const availableTotal = num(ticketsAvailable)
  const overallStatus = standing(perc)

  const tIncome = num(fin.todayincome)
  const yIncome = num(fin.yesterdayincome)
  const tSales = num(fin.todaysales)
  const ySales = num(fin.yesterdaysales)
  const incomeDelta = change(tIncome, yIncome)
  const salesDelta = change(tSales, ySales)

  const noSales = num(fin.todayPerformance) <= 0 && num(fin.yesterdayPerformance) <= 0
  const perfDiff = num(fin.todayPerformance) - num(fin.yesterdayPerformance)
  const summary = noSales
    ? { text: 'No sales yesterday or today', color: C.down, bg: 'rgba(229,72,77,.08)', value: '' }
    : perfDiff >= 0
      ? {
          text: 'Sales are up on yesterday',
          color: C.up,
          bg: 'rgba(18,160,107,.09)',
          value: `+${perfDiff.toFixed(1)}%`,
        }
      : {
          text: 'Sales are down on yesterday',
          color: C.down,
          bg: 'rgba(229,72,77,.08)',
          value: `-${Math.abs(perfDiff).toFixed(1)}%`,
        }

  /* ---------- chart data ---------- */
  const labels = tiers.map((t) => t.name)
  const colors = tiers.map((t) => t.color)

  const revenueDonut = {
    labels,
    datasets: [
      {
        data: tiers.map((t) => t.revenue),
        backgroundColor: colors,
        borderWidth: 3,
        borderColor: '#fff',
      },
    ],
  }

  const salesDonut = {
    labels,
    datasets: [
      {
        data: tiers.map((t) => t.sold),
        backgroundColor: colors,
        borderWidth: 3,
        borderColor: '#fff',
      },
    ],
  }

  const stackedInventory = {
    labels,
    datasets: [
      {
        label: 'Sold',
        data: tiers.map((t) => t.sold),
        backgroundColor: colors,
        borderRadius: 4,
        barThickness: 22,
      },
      {
        label: 'Remaining',
        data: tiers.map((t) => t.remaining),
        backgroundColor: C.track,
        barThickness: 22,
      },
    ],
  }

  const tierPerformance = {
    labels,
    datasets: [
      {
        label: metric === 'revenue' ? 'Revenue (R)' : 'Tickets sold',
        data: tiers.map((t) => (metric === 'revenue' ? t.revenue : t.sold)),
        backgroundColor: colors,
        borderRadius: 6,
        barThickness: 44,
      },
    ],
  }

  const movementChart = {
    labels: ['Yesterday', 'Today'],
    datasets: [
      {
        label: 'Revenue (R)',
        data: [yIncome, tIncome],
        backgroundColor: ['rgba(47,111,237,.25)', C.vip],
        borderRadius: 6,
        barThickness: 46,
        yAxisID: 'rev',
      },
      {
        type: 'line',
        label: 'Tickets sold',
        data: [ySales, tSales],
        borderColor: C.gen,
        backgroundColor: C.gen,
        pointBackgroundColor: '#fff',
        pointBorderColor: C.gen,
        pointBorderWidth: 3,
        pointRadius: 6,
        borderWidth: 3,
        fill: false,
        yAxisID: 'cnt',
      },
    ],
  }

  const fillTrend = {
    labels,
    datasets: [
      {
        label: 'Sell-through %',
        data: tiers.map((t) => t.rate),
        borderColor: C.ink,
        backgroundColor: 'rgba(20,33,61,.08)',
        pointBackgroundColor: colors,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 7,
        borderWidth: 3,
        lineTension: 0.35,
        fill: true,
      },
    ],
  }

  /* ---------- chart options ---------- */
  const donutOptions = (fmt) => ({
    maintainAspectRatio: false,
    cutoutPercentage: 72,
    legend: { display: false },
    tooltips: {
      ...tip,
      callbacks: {
        label: (item, data) =>
          ` ${data.labels[item.index]}: ${fmt(data.datasets[0].data[item.index])}`,
      },
    },
  })

  const inventoryOptions = {
    maintainAspectRatio: false,
    legend: {
      position: 'bottom',
      labels: { ...font, usePointStyle: true, boxWidth: 8, padding: 16 },
    },
    tooltips: { ...tip, mode: 'index', intersect: false },
    scales: {
      xAxes: [{ stacked: true, gridLines: gridX, ticks: font }],
      yAxes: [
        { stacked: true, gridLines: gridY, ticks: { ...font, beginAtZero: true, precision: 0 } },
      ],
    },
  }

  const tierOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      ...tip,
      callbacks: {
        label: (item) =>
          metric === 'revenue' ? ` ${money(item.yLabel)}` : ` ${item.yLabel} tickets`,
      },
    },
    scales: {
      xAxes: [{ gridLines: gridX, ticks: font }],
      yAxes: [
        {
          gridLines: gridY,
          ticks: {
            ...font,
            beginAtZero: true,
            precision: 0,
            callback: (v) => (metric === 'revenue' ? compact(v) : v),
          },
        },
      ],
    },
  }

  const movementOptions = {
    maintainAspectRatio: false,
    legend: {
      position: 'bottom',
      labels: { ...font, usePointStyle: true, boxWidth: 8, padding: 16 },
    },
    tooltips: {
      ...tip,
      mode: 'index',
      intersect: false,
      callbacks: {
        label: (item, data) => {
          const ds = data.datasets[item.datasetIndex]
          return ds.yAxisID === 'rev'
            ? ` Revenue: ${money(item.yLabel)}`
            : ` Tickets: ${item.yLabel}`
        },
      },
    },
    scales: {
      xAxes: [{ gridLines: gridX, ticks: font }],
      yAxes: [
        {
          id: 'rev',
          position: 'left',
          gridLines: gridY,
          ticks: { ...font, beginAtZero: true, callback: (v) => compact(v) },
        },
        {
          id: 'cnt',
          position: 'right',
          gridLines: { display: false, drawBorder: false },
          ticks: { ...font, beginAtZero: true, precision: 0 },
        },
      ],
    },
  }

  const trendOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      ...tip,
      callbacks: { label: (item) => ` ${Number(item.yLabel).toFixed(1)}% sold` },
    },
    scales: {
      xAxes: [{ gridLines: gridX, ticks: font }],
      yAxes: [
        {
          gridLines: gridY,
          ticks: { ...font, beginAtZero: true, max: 100, stepSize: 25, callback: (v) => `${v}%` },
        },
      ],
    },
  }

  return (
    <div className="view">
      <style>{styles}</style>
      {localStorage.getItem('businessid') === null ? <Redirect to="/login" /> : ''}
      <Header />
      <SideNav collapsed={collapsed} onToggle={handleToggle} />

      <div
        className="container"
        style={{
          marginTop: 40,
          marginLeft: sidebarOffset,
          width: `calc(100% - ${sidebarOffset}px)`,
          maxWidth: 'none',
          paddingLeft: 16,
          paddingRight: 16,
          transition: 'margin-left .25s ease, width .25s ease',
        }}
      >
        {isMob && !collapsed && <div className="mobile-overlay active" onClick={handleToggle} />}
        {isMob && collapsed && (
          <label className="mobile-toggle" onClick={handleToggle}>
            <BiMenu size={25} />
          </label>
        )}

        <div className="an">
          <button type="button" className="an-back" onClick={() => history.goBack()}>
            <IoIosArrowBack size={18} />
            Back
          </button>

          {/* ---------- header ---------- */}
          <div className="an-head">
            <div>
              <h1 className="an-title">{st.eventname}</h1>
              <p className="an-meta">{[st.eventdate, st.venue].filter(Boolean).join('  |  ')}</p>
            </div>
            <span className="an-badge">
              <span className="an-dot" style={{ background: overallStatus.color }} />
              Sales performance: {overallStatus.label}
            </span>
          </div>

          {/* ---------- KPIs ---------- */}
          <div className="an-grid an-kpis">
            <div className="an-card">
              <div className="an-kpi-l">Revenue</div>
              <div className="an-kpi-v">{money(revenueTotal)}</div>
              <div className="an-kpi-f">
                Today {money(tIncome)} <Delta d={incomeDelta} />
              </div>
            </div>
            <div className="an-card">
              <div className="an-kpi-l">Tickets sold</div>
              <div className="an-kpi-v">
                {soldTotal}
                <span style={{ fontSize: 16, color: C.muted, fontWeight: 600 }}>
                  {' '}
                  / {availableTotal}
                </span>
              </div>
              <div className="an-kpi-f">
                Today {tSales} <Delta d={salesDelta} />
              </div>
            </div>
            <div className="an-card">
              <div className="an-kpi-l">Sell-through</div>
              <div className="an-kpi-v" style={{ color: overallStatus.color }}>
                {percentage}
              </div>
              <div className="an-track" style={{ marginTop: 14 }}>
                <div
                  className="an-fill"
                  style={{ width: percentage, background: overallStatus.color }}
                />
              </div>
            </div>
            <div className="an-card">
              <div className="an-kpi-l">Performance index</div>
              <div className="an-kpi-v">{num(fin.overallindicators)}%</div>
              <div className="an-kpi-f">
                Yesterday {num(fin.yesterdayPerformance)}% &nbsp;|&nbsp; Today{' '}
                {num(fin.todayPerformance)}%
              </div>
            </div>
          </div>

          {/* ---------- revenue + tier breakdown ---------- */}
          <div className="an-grid an-two">
            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Revenue mix</h2>
                <span className="an-card-s">Share by ticket type</span>
              </div>
              <div className="an-donut">
                <Doughnut data={revenueDonut} options={donutOptions((v) => money(v))} />
                <div className="an-donut-c">
                  <b>{money(revenueTotal)}</b>
                  <span>Total revenue</span>
                </div>
              </div>
              <div className="an-legend">
                {tiers.map((t) => (
                  <div key={t.key} style={{ borderColor: t.color }}>
                    <small>{t.name}</small>
                    <b>{money(t.revenue)}</b>
                  </div>
                ))}
              </div>
            </div>

            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Tickets sold mix</h2>
                <span className="an-card-s">Share by ticket type</span>
              </div>
              <div className="an-donut">
                <Doughnut data={salesDonut} options={donutOptions((v) => `${v} tickets`)} />
                <div className="an-donut-c">
                  <b>{soldTotal}</b>
                  <span>Tickets sold</span>
                </div>
              </div>
              <div className="an-legend">
                {tiers.map((t) => (
                  <div key={t.key} style={{ borderColor: t.color }}>
                    <small>{t.name}</small>
                    <b>{t.sold} tickets</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---------- tier comparison ---------- */}
          <div className="an-grid an-three">
            <div className="an-card">
              <div className="an-card-h">
                <div>
                  <h2 className="an-card-t">Ticket type comparison</h2>
                  <span className="an-card-s">
                    {metric === 'revenue' ? 'Revenue' : 'Tickets sold'} per type
                  </span>
                </div>
                <div className="an-toggle" role="group" aria-label="Metric">
                  <button
                    type="button"
                    className={`an-tab ${metric === 'revenue' ? 'on' : ''}`}
                    onClick={() => setMetric('revenue')}
                  >
                    Revenue
                  </button>
                  <button
                    type="button"
                    className={`an-tab ${metric === 'sold' ? 'on' : ''}`}
                    onClick={() => setMetric('sold')}
                  >
                    Tickets
                  </button>
                </div>
              </div>
              <div className="an-chart tall">
                <Bar data={tierPerformance} options={tierOptions} />
              </div>
            </div>

            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Sales progress</h2>
                <span className="an-card-s">
                  {soldTotal}/{availableTotal} sold
                </span>
              </div>
              {tiers.map((t) => {
                const s = standing(t.rate)
                return (
                  <div className="an-tier" key={t.key}>
                    <div className="an-tier-top">
                      <span className="an-tier-n">
                        <span className="an-dot" style={{ background: t.color }} />
                        {t.name}
                      </span>
                      <span className="an-tier-p" style={{ color: s.color, background: s.bg }}>
                        {s.label}
                      </span>
                    </div>
                    <div className="an-tier-sub">
                      <span>
                        {t.sold}/{t.total} sold
                      </span>
                      <span>{money(t.revenue)}</span>
                    </div>
                    <div className="an-track">
                      <div
                        className="an-fill"
                        style={{ width: `${Math.min(t.rate, 100)}%`, background: t.color }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* ---------- inventory + sell-through ---------- */}
          <div className="an-grid an-two">
            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Inventory</h2>
                <span className="an-card-s">Sold vs remaining</span>
              </div>
              <div className="an-chart">
                <Bar data={stackedInventory} options={inventoryOptions} />
              </div>
            </div>

            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Sell-through by type</h2>
                <span className="an-card-s">% of allocation sold</span>
              </div>
              <div className="an-chart">
                <Line data={fillTrend} options={trendOptions} />
              </div>
            </div>
          </div>

          {/* ---------- yesterday vs today ---------- */}
          <div className="an-grid an-two">
            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Yesterday vs today</h2>
                <span className="an-card-s">Revenue and tickets</span>
              </div>
              <div className="an-chart">
                <Bar data={movementChart} options={movementOptions} />
              </div>
            </div>

            <div className="an-card">
              <div className="an-card-h">
                <h2 className="an-card-t">Sales movement</h2>
                <span className="an-card-s">Overall, yesterday, today</span>
              </div>
              <table className="an-movement">
                <thead>
                  <tr>
                    <th />
                    <th>Overall</th>
                    <th>Yesterday</th>
                    <th>Today</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Revenue</td>
                    <td>{money(fin.overallincome)}</td>
                    <td>{money(yIncome)}</td>
                    <td
                      style={{
                        color:
                          incomeDelta.dir === 'down'
                            ? C.down
                            : incomeDelta.dir === 'up'
                              ? C.up
                              : C.ink,
                      }}
                    >
                      {money(tIncome)}
                    </td>
                  </tr>
                  <tr>
                    <td>Tickets</td>
                    <td>{num(fin.overallsales)}</td>
                    <td>{ySales}</td>
                    <td
                      style={{
                        color:
                          salesDelta.dir === 'down'
                            ? C.down
                            : salesDelta.dir === 'up'
                              ? C.up
                              : C.ink,
                      }}
                    >
                      {tSales}
                    </td>
                  </tr>
                  <tr>
                    <td>Index</td>
                    <td>{num(fin.overallindicators)}%</td>
                    <td>{num(fin.yesterdayPerformance)}%</td>
                    <td>{num(fin.todayPerformance)}%</td>
                  </tr>
                </tbody>
              </table>
              <div className="an-summary" style={{ background: summary.bg, color: summary.color }}>
                {summary.text}
                {summary.value && <b>{summary.value}</b>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics
